import React from 'react';
import PropTypes from 'prop-types';
import { Typography, ExpandCollapse } from '@telus-uds/components-web';
import { evaluateFieldVisibility } from '@mobilelive-inc/telus-forms-core';
import { GroupWrapper, GroupTitle } from '../css';
import RichTextNote from '../../note';
import { VulcanFormField } from './fieldRegistry';
import { isVulcanItemHidden } from './vulcanItemHides';
import { VulcanSectionChromeContext } from './vulcanSectionChromeContext';

/**
 * Live declarative visibility for a content-type entry's OWN gate, parity of
 * formStepVulcan's generic `isRelatedFieldExisted(entry.fields, formFields, …)`
 * check — which the real app applies UNIFORMLY to every content type
 * (formStepVulcan.js: `if (!isRelatedFieldExisted(fields, formFields, orderData,
 * isFormTouched)) return null;`, BEFORE the id/kind branching). Runs the engine's
 * declarative visibility interpreter on the entry's OWN
 * appearsAfterField/Value/backendFieldValidation (on `item.raw`) against the live
 * engine state (`form.values` + a visibility map from `form.fieldStates`,
 * available since forms-core alpha.5 passes `form` to structural components).
 * The non-live context (orderData/isFormTouched/multiSelect/backendMode) that
 * lives in formStepVulcan's scope is supplied by the caller. An entry with no
 * gate evaluates visible (unchanged) — exported so both the collapsible
 * `Section` (`formSection`) AND the content-type-section registry wrappers
 * (`userDetails`/`equipment`/`repeatedForm`, in vulcanSectionRegistry.js) can
 * apply the SAME gate: unlike leaf `field`/`section` kinds, the engine's `custom`
 * kind (FormRunner.js `case 'custom'`) dispatches straight to the sectionRegistry
 * component with NO declarative-visibility check of its own — so without this,
 * a content-type section whose OWN appearsAfterField/Value condition isn't met
 * would always render regardless (confirmed real-data bug: order 83480's
 * "Service Address" userDetails section, gated to not apply on step 1, rendered
 * unconditionally under the harness while correctly hidden in the real app).
 */
export const deriveDeclarativeVisibility = (raw, form, visibilityContext) => {
  if (!form) return true;
  const visibility = {};
  const fieldStates = form.fieldStates || {};
  Object.keys(fieldStates).forEach(k => {
    visibility[k] = fieldStates[k].isVisible;
  });
  return evaluateFieldVisibility(raw, {
    values: form.values || {},
    visibility,
    ...(visibilityContext || {})
  });
};

/**
 * Step-level chrome the collapsible `Section` needs but the engine doesn't own —
 * it lives in `formStepVulcan`'s component scope (the ExpandCollapse accordion
 * state, which sections are visible, the role-prefixed control label, the step-4
 * order-summary slot). The step-integration harness wraps `<FormRunner>` in
 * `<ExpandCollapse>` and provides this via the context, so `Section` renders the
 * exact same `ExpandCollapse.Panel` as today. Absent a provider (e.g. a section
 * rendered outside the accordion harness), `Section` degrades to a plain titled
 * block rather than throwing. The context itself lives in
 * `vulcanSectionChromeContext.js` (re-exported here for existing importers) —
 * `fieldRegistry.js` needs it too (for `MultiFileUpload`'s `orderData`), and
 * this module already imports `fieldRegistry.js`, so defining it here would
 * be an import cycle.
 *
 * @typedef {import('./vulcanSectionChromeContext').VulcanSectionChrome} VulcanSectionChrome
 */
export { VulcanSectionChromeContext };

/**
 * Vulcan's imperative, name-matched hides applied to a STRUCTURAL item.
 * `renderFormFields` ran these before its content-type branching, so groups,
 * section titles and notes were subject to them exactly like leaf fields; the
 * engine has no equivalent for non-leaf items, so they are applied here.
 */
const useItemHidden = (item, form) => {
  const chrome = React.useContext(VulcanSectionChromeContext) || {};
  const ctx = chrome.visibilityContext || {};
  return isVulcanItemHidden(item && item.raw, {
    form,
    orderData: ctx.orderData,
    salesRepOutlet: chrome.salesRepOutlet
  });
};

// The engine's `item.raw` is the Contentful entry's `fields` — an open object;
// we only read `title` / `content`. A specific shape keeps eslint's
// forbid-prop-types happy (bare `PropTypes.object` is banned).
const itemShape = PropTypes.shape({
  raw: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
  })
});

/**
 * Vulcan's structural-component overrides for `<FormRunner>` (the `components`
 * prop). These replace the hand-written structural branches of
 * `formStepVulcan.js`'s `renderFormFields` with the exact same DOM, so the engine
 * renders groups / section-titles / notes byte-for-byte as today.
 *
 * The engine calls each with `{ item, children }` where `item.raw` is the
 * Contentful entry's `fields` and `children` are the already-rendered child
 * items (hidden fields collapse to `null`).
 *
 * `Section` (the collapsible `ExpandCollapse.Panel`) is here too, reading the
 * step-level accordion chrome from `VulcanSectionChromeContext` (see above) so it
 * stays a structural component the engine dispatches, while the chrome that lives
 * in `formStepVulcan`'s scope is injected by the step-integration harness.
 */

/**
 * `formFieldsGroup` → the `GroupWrapper` (+ optional `GroupTitle`). Mirrors
 * Vulcan's rule of not rendering an empty group: if every child collapsed to
 * `null` (all hidden), render nothing rather than an empty wrapper. Replaces the
 * `groupIsVisible` pre-check that Vulcan ran before drawing the wrapper.
 */
function Group({ item, children, form }) {
  const hidden = useItemHidden(item, form);
  const visibleChildren = React.Children.toArray(children);
  if (hidden) return null;
  if (visibleChildren.length === 0) return null;
  const title = item && item.raw && item.raw.title;
  return (
    <GroupWrapper>
      {title && <GroupTitle>{title}</GroupTitle>}
      {visibleChildren}
    </GroupWrapper>
  );
}
Group.propTypes = {
  item: itemShape.isRequired,
  children: PropTypes.node,
  form: PropTypes.shape({})
};
Group.defaultProps = { children: null, form: null };

/** `formSectionTitle` → a plain `<Typography h3>` heading. */
function SectionTitle({ item, form }) {
  const hidden = useItemHidden(item, form);
  const title = item && item.raw && item.raw.title;
  if (hidden) return null;
  return (
    <Typography heading="h3" variant={{ size: 'h3' }} block>
      {title}
    </Typography>
  );
}
SectionTitle.propTypes = {
  item: itemShape.isRequired,
  form: PropTypes.shape({})
};
SectionTitle.defaultProps = { form: null };

/**
 * Standalone `note` content type → a `TextNote` field, exactly as Vulcan did
 * (`<FormField data={{ type: 'TextNote', note: fields.content, isShowNoteBackground: true }} />`).
 */
function Note({ item, form }) {
  const hidden = useItemHidden(item, form);
  const content = item && item.raw && item.raw.content;
  if (hidden) return null;
  return (
    <VulcanFormField
      data={{ type: 'TextNote', note: content, isShowNoteBackground: true }}
    />
  );
}
Note.propTypes = {
  item: itemShape.isRequired,
  form: PropTypes.shape({})
};
Note.defaultProps = { form: null };

/**
 * `formSection` → the collapsible `ExpandCollapse.Panel`, a 1:1 port of
 * formStepVulcan.js's `formSection` branch. The accordion chrome comes from
 * `VulcanSectionChromeContext` (injected by the step harness); the section's own
 * `note` (a field on the section, not a child) renders inside the panel, then the
 * engine-rendered `children`, then the optional step-level extras
 * (VulcanOrderSummary on step 4). Parity gate: render only when the section has a
 * `title` AND `isSectionVisible(name)` — matching `title && sectionIsVisible(...)`.
 * With no chrome provider it degrades to a plain titled block.
 */
function Section({ item, children, form }) {
  const chrome = React.useContext(VulcanSectionChromeContext) || {};
  const raw = (item && item.raw) || {};
  const { name, title, note } = raw;
  const {
    expandProps,
    isSectionVisible,
    visibilityContext,
    formatControl,
    onToggle,
    renderSectionExtras
  } = chrome;

  if (!title) return null;
  if (
    isVulcanItemHidden(raw, {
      form,
      orderData: (visibilityContext || {}).orderData,
      salesRepOutlet: chrome.salesRepOutlet
    })
  ) {
    return null;
  }
  // Visibility: an explicit chrome `isSectionVisible` override wins (escape
  // hatch); otherwise derive live from the engine `form` (alpha.5).
  const visible = isSectionVisible
    ? isSectionVisible(name)
    : deriveDeclarativeVisibility(raw, form, visibilityContext);
  if (!visible) return null;

  const control = formatControl ? (
    formatControl(title, name)
  ) : (
    <Typography variant={{ size: 'h2' }}>{title}</Typography>
  );
  const renderedNote = note && note.content && (
    <RichTextNote
      data={note}
      type="richText"
      isShowNoteBackground={raw.isShowNoteBackground}
      isWarningMessage={raw.isWarningMessage}
      isColorRed={raw.isColorRed}
      isItalicNote={raw.isItalicNote}
    />
  );
  const body = (
    <>
      {renderedNote}
      {children}
      {renderSectionExtras && renderSectionExtras(name)}
    </>
  );

  // No accordion chrome available — degrade to a plain titled block.
  if (!expandProps) {
    return (
      <section data-vulcan-section={name}>
        <Typography variant={{ size: 'h2' }}>{title}</Typography>
        {body}
      </section>
    );
  }

  return (
    <ExpandCollapse.Panel
      {...expandProps}
      onToggle={e => {
        if (typeof expandProps.onToggle === 'function') expandProps.onToggle(e);
        if (onToggle) onToggle(name, e);
      }}
      panelId={name}
      control={control}
    >
      {body}
    </ExpandCollapse.Panel>
  );
}
Section.propTypes = {
  item: itemShape.isRequired,
  children: PropTypes.node,
  // The live engine (forms-core alpha.5). Loose shape — it is the whole
  // useFormRunner API; Section reads `values` / `fieldStates`.
  form: PropTypes.shape({
    values: PropTypes.shape({}),
    fieldStates: PropTypes.shape({})
  })
};
Section.defaultProps = { children: null, form: null };

/**
 * The structural `components` override for `<FormRunner>`: Group / SectionTitle /
 * Note / Section, matching formStepVulcan.js byte-for-byte. Section additionally
 * consumes `VulcanSectionChromeContext` for the step-level accordion chrome.
 *
 * @type {{Group: Function, SectionTitle: Function, Note: Function, Section: Function}}
 */
export const vulcanStructuralComponents = {
  Group,
  SectionTitle,
  Note,
  Section
};

export default vulcanStructuralComponents;
